import { useMemo, useEffect } from 'react';

import { useVisitorCustomFieldConfigs } from './useVisitorCustomFieldConfigs';
import {
  BASE_FIELD_KEYS,
  type FormCustomFieldColumn,
} from '../flatfile/workbook';

/** Stable empty array reference so consumers avoid unnecessary re-renders when form has no custom fields. */
const EMPTY_FORM_CUSTOM_FIELD_COLUMNS: FormCustomFieldColumn[] = [];

/**
 * Resolves form custom field configs for the registration form so CSV/Flatfile template
 * can include only the custom fields added to the form (same order as form).
 * When form has no custom fields, returns [] (stable reference).
 * Fetches when organizationId and customFieldIds are present; server may return [] if feature is off.
 */
export function useFormCustomFieldColumns(
  organizationId: string | undefined,
  customFieldIds: string[] | undefined
): {
  formCustomFieldColumns: FormCustomFieldColumn[];
  loading: boolean;
} {
  const { data: allConfigs, loading, fetch } =
    useVisitorCustomFieldConfigs();

  const shouldFetch =
    Boolean(organizationId) && Boolean(customFieldIds?.length);

  useEffect(() => {
    if (shouldFetch && organizationId) {
      fetch(organizationId);
    }
  }, [shouldFetch, organizationId, fetch]);

  const formCustomFieldColumns = useMemo((): FormCustomFieldColumn[] => {
    if (!customFieldIds?.length || !allConfigs.length) {
      return EMPTY_FORM_CUSTOM_FIELD_COLUMNS;
    }
    const customFieldConfigById = new Map(
      allConfigs.map(config => [config.id, config])
    );
    const columns = customFieldIds
      .map(id => customFieldConfigById.get(id))
      .filter((config): config is NonNullable<typeof config> => config != null)
      .filter(config => config.isEnabled)
      .filter(config => !BASE_FIELD_KEYS.has(config.fieldKey))
      .map(config => ({
        fieldKey: config.fieldKey,
        fieldName: config.fieldName,
      }));
    return columns.length === 0 ? EMPTY_FORM_CUSTOM_FIELD_COLUMNS : columns;
  }, [customFieldIds, allConfigs]);

  return {
    formCustomFieldColumns,
    loading: shouldFetch && loading,
  };
}
